import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { drawCrosshairOnContext, parseCrosshairCommands } from './Crosshair.utils';

const CanvasContainer = styled.div`
    width: 100%;
    height: 100%;
    //background: linear-gradient(180deg, rgba(29, 38, 113, 0.1) 0%, rgba(15, 23, 42, 0.9) 100%);
    background: radial-gradient(circle, rgba(29, 38, 113, 0.1) 0%, rgba(0, 0, 0, 0.5) 200%);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

const StyledCanvas = styled.canvas`
    display: block;
    image-rendering: pixelated;
    image-rendering: crisp-edges;
`;

const CrosshairMiniPreview = ({ commands }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const width = 130;
        const height = 130;
        const dpr = window.devicePixelRatio || 1;

        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        const params = parseCrosshairCommands(commands);
        let animationFrameId;
        const startTime = Date.now();

        const isDynamic = params.style === 5;
        const hasRecoil = !!params.recoil;

        const render = () => {
            const time = Date.now() - startTime;

            ctx.save();
            ctx.scale(dpr, dpr);
            ctx.clearRect(0, 0, width, height);

            const drawParams = { ...params };

            // Dynamic style: pulse the gap outward
            if (isDynamic) {
                const pulse = Math.abs(Math.sin(time / 180)) * 5;
                drawParams.gap = params.gap + pulse;
            }

            // Recoil: animate crosshair position upward with lateral jitter
            let recoilX = 0;
            let recoilY = 0;
            if (hasRecoil) {
                const CYCLE = 2000;
                const BURST_END = 500;
                const RETURN_END = 950;
                const t = time % CYCLE;

                if (t <= BURST_END) {
                    const shotNum = Math.min(Math.floor(t / 100), 4);
                    const localT = t % 100;
                    const ySteps = [0, 4, 8, 12, 14.5, 16];
                    const xSteps = [0, 2, -2.5, 1.5, -2, 2.5];
                    const yFrom = ySteps[shotNum];
                    const yTo = ySteps[shotNum + 1];
                    const xFrom = xSteps[shotNum];
                    const xTo = xSteps[shotNum + 1];
                    const eased = 1 - Math.pow(1 - localT / 100, 2);
                    recoilX = xFrom + (xTo - xFrom) * eased;
                    recoilY = -(yFrom + (yTo - yFrom) * eased);
                } else if (t <= RETURN_END) {
                    const returnProgress = (t - BURST_END) / (RETURN_END - BURST_END);
                    const eased = 1 - Math.pow(1 - returnProgress, 3);
                    recoilX = 2.5 * (1 - eased);
                    recoilY = -16 * (1 - eased);
                }
            }

            ctx.save();
            ctx.translate(recoilX, recoilY);
            drawCrosshairOnContext(ctx, width, height, drawParams);
            ctx.restore();

            ctx.restore();

            if (isDynamic || hasRecoil) {
                animationFrameId = requestAnimationFrame(render);
            }
        };

        if (isDynamic || hasRecoil) {
            animationFrameId = requestAnimationFrame(render);
        } else {
            render();
        }

        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [commands]);

    return (
        <CanvasContainer ref={containerRef}>
            <StyledCanvas ref={canvasRef} />
        </CanvasContainer>
    );
};

export default CrosshairMiniPreview;
